import { useEffect, type FC } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate
} from "react-router-dom";
import NewConversationView from "./NewConversationView";
import ConversationDetailView from "./ConversationDetailView";
import UpdatesView from "./UpdatesView";
import { useBlocNext } from "@blac/react";
import UserPreferencesCubit, {
  UserPreferenceKeys
} from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import ChatDrawerLayout from "./ChatDrawerLayout";
import ConversationListCubit from "./state/ConversationListCubit";
import LeapChatRouterCubit from "./state/LeapChatRouterCubit";
import { ChatTourVariant } from "./state/chatTour";
import { DRAWER_DESKTOP_BREAKPOINT } from "./leapChat.styles";
import { useMediaQuery } from "@react-hooks-library/core";
import { APP_CHAT_PATH } from "src/lib/createAppPath";
import { LeapChatPath } from "./leapChatPaths";

const ChatIndexRedirect: FC = () => {
  const [
    {
      checking,
      autoSelectPath,
      lastChatPath,
      showAnnouncement,
      shouldOpenDrawer
    },
    {
      resolveIndexRedirect,
      showSophieAnnouncement,
      startTourFromSource,
      markConversationOnboarded,
      clearShouldOpenDrawer
    }
  ] = useBlocNext(LeapChatRouterCubit);
  const [, conversationListCubit] = useBlocNext(ConversationListCubit);
  const navigate = useNavigate();
  const isMobile = useMediaQuery(`(max-width: ${DRAWER_DESKTOP_BREAKPOINT}px)`);

  useEffect(() => {
    resolveIndexRedirect();
  }, [resolveIndexRedirect]);

  useEffect(() => {
    if (shouldOpenDrawer && isMobile) {
      conversationListCubit.openDrawer();
      clearShouldOpenDrawer();
    }
  }, [
    shouldOpenDrawer,
    isMobile,
    conversationListCubit,
    clearShouldOpenDrawer
  ]);

  const isOnNewConversationView =
    !checking &&
    !autoSelectPath &&
    !(lastChatPath && lastChatPath !== APP_CHAT_PATH);

  useEffect(() => {
    if (isOnNewConversationView && showAnnouncement && isMobile) {
      showSophieAnnouncement(
        () => {
          markConversationOnboarded();
          startTourFromSource(ChatTourVariant.OnlyOldMessages, navigate);
        },
        () => {
          markConversationOnboarded();
        }
      );
    }
  }, [
    isOnNewConversationView,
    showAnnouncement,
    showSophieAnnouncement,
    startTourFromSource,
    markConversationOnboarded,
    navigate,
    isMobile
  ]);

  if (checking) {
    return null;
  }

  if (autoSelectPath) {
    return <Navigate to={autoSelectPath} replace />;
  }

  if (lastChatPath && lastChatPath !== APP_CHAT_PATH) {
    return <Navigate to={lastChatPath} replace />;
  }

  return <NewConversationView />;
};

const LeapChatRouter: FC = () => {
  const [, { initTourGuide: onMount, trackPath, setConversationHistoryId }] =
    useBlocNext(LeapChatRouterCubit);
  const [preferencesState] = useBlocNext(UserPreferencesCubit);
  const location = useLocation();

  useEffect(() => {
    return onMount();
  }, [onMount]);

  useEffect(() => {
    trackPath(location.pathname);
  }, [trackPath, location.pathname]);

  const conversationHistoryId =
    preferencesState[UserPreferenceKeys.previousMessagesConversationId];

  useEffect(() => {
    if (conversationHistoryId) {
      setConversationHistoryId(conversationHistoryId);
    }
  }, [conversationHistoryId, setConversationHistoryId]);

  return (
    <ChatDrawerLayout>
      <Routes>
        <Route index element={<ChatIndexRedirect />} />
        <Route path={LeapChatPath.new} element={<NewConversationView />} />
        <Route path={LeapChatPath.updates} element={<UpdatesView />} />
        <Route
          path={LeapChatPath.conversation}
          element={<ConversationDetailView />}
        />
        <Route path="*" element={<Navigate to={APP_CHAT_PATH} replace />} />
      </Routes>
    </ChatDrawerLayout>
  );
};

export default LeapChatRouter;
